import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { useTheme } from '@emotion/react'
import { graphql, useStaticQuery } from 'gatsby'
import { ListItem, List } from 'chaoskit/src/components'
import { kebabCase, toLower } from 'lodash'

import Data from '~components/Data'
import Image from '~components/Image'
import TextLink from '~components/TextLink'
import { micrositeSectionTitle } from '~styles/utility'

const ResourceItem = ({ item }) => {
  const theme = useTheme()

  return (
    <ListItem>
      <TextLink
        href={item.link}
        css={{
          fontWeight: theme.fontWeight.bold,
          color: theme.fontColor.base,
        }}
      >
        <Data>{item.title}</Data>
      </TextLink>
    </ListItem>
  )
}

ResourceItem.propTypes = {
  item: PropTypes.object.isRequired,
}

const ChaosMonkeyResources = () => {
  const theme = useTheme()

  const {
    file: {
      childMdx: { frontmatter: pageData },
    },
  } = useStaticQuery(graphql`
    query ChaosMonkeyResourcesPageData {
      file(
        name: { eq: "chaos-monkey__resources" }
        absolutePath: { regex: "/src/data/" }
      ) {
        childMdx {
          frontmatter {
            resources {
              heading
              blurb
              items {
                logo
                link
                title
              }
            }
          }
        }
      }
    }
  `)

  return pageData.resources.map((resource) => {
    const hasLogos = resource.items[0].logo

    return (
      <Fragment key={resource.heading}>
        <h3
          id={kebabCase(toLower(resource.heading))}
          css={micrositeSectionTitle(theme, {
            align: 'center',
          })}
        >
          <Data>{resource.heading}</Data>
        </h3>
        <div css={{ textAlign: 'center', marginBottom: theme.space.large }}>
          <Data>{resource.blurb}</Data>
        </div>
        {hasLogos ? (
          <div
            css={{
              display: 'grid',
              gap: theme.space.base,

              [theme.mq.small]: {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },

              [theme.mq.medium]: {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
            }}
          >
            {resource.items.map((item) => (
              <TextLink
                key={item.title}
                href={item.link}
                css={{
                  display: 'grid',
                  width: '100%',
                  gap: theme.space.base,
                  gridTemplateColumns: 'auto 1fr',
                  alignItems: 'center',
                  color: theme.fontColor.base,
                  transformOrigin: 'center center',
                  transition: `color ${theme.timing.base} ${theme.transition.bounce}, transform ${theme.timing.base} ${theme.transition.bounce}`,
                  padding: theme.space.base,
                  boxShadow: theme.boxShadow.large,
                  fontWeight: theme.fontWeight.bold,

                  '&:hover, &:focus': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Image
                  css={{
                    maxHeight: theme.height.base,
                    width: theme.height.base,
                    textAlign: 'center',
                  }}
                  src={item.logo}
                  alt={item.title}
                />
                <span>
                  <Data>{item.title}</Data>
                </span>
              </TextLink>
            ))}
          </div>
        ) : (
          <List
            type="circles"
            space="small"
            css={{
              [theme.mq.small]: {
                gridTemplateColumns: '1fr 1fr',
                gap: theme.space.base,
              },

              [theme.mq.medium]: {
                gridTemplateColumns: '1fr 1fr 1fr',
              },
            }}
          >
            {resource.items.map((item) => (
              <ResourceItem key={item.title} item={item} />
            ))}
          </List>
        )}
      </Fragment>
    )
  })
}

export default ChaosMonkeyResources
